import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { RM } from '../lib/rm';

const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p,0.,1.); }`;

const FRAG = `
precision highp float;
uniform vec2 u_res; uniform float u_time; uniform vec2 u_mouse; uniform float u_amp;
float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123); }
float noise(vec2 p){ vec2 i=floor(p),f=fract(p); vec2 u=f*f*(3.-2.*f);
  return mix(mix(hash(i),hash(i+vec2(1,0)),u.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),u.x),u.y); }
float fbm(vec2 p){ float v=0.,a=.5; mat2 m=mat2(1.6,1.2,-1.2,1.6);
  for(int i=0;i<4;i++){ v+=a*noise(p); p=m*p; a*=.5; } return v; }
void main(){
  vec2 uv=gl_FragCoord.xy/u_res.xy; vec2 asp=vec2(u_res.x/u_res.y,1.);
  vec2 p=(uv-.5)*asp*2.4; float t=u_time*0.045;
  vec2 q=vec2(fbm(p+t), fbm(p+vec2(5.2,1.3)-t));
  vec2 r=vec2(fbm(p+1.8*q+vec2(1.7,9.2)+t*.6), fbm(p+1.8*q+vec2(8.3,2.8)-t*.4));
  float f=fbm(p+2.0*r);
  float md=distance(uv*asp,u_mouse*asp); float glow=smoothstep(.55,0.,md);
  vec3 base=vec3(0.047,0.063,0.075);
  vec3 amber=vec3(0.91,0.70,0.29); vec3 cyan=vec3(0.34,0.81,0.79);
  vec3 col=base;
  col=mix(col, base+vec3(0.030,0.036,0.042), f);
  col+=amber*pow(f,3.0)*0.42;
  col+=cyan*pow(clamp(r.x*.5+.5,0.,1.),4.0)*0.12;
  col+=amber*glow*0.16*u_amp;
  col*=1.0-0.42*distance(uv,vec2(.5));
  gl_FragColor=vec4(col,1.);
}`;

export function FluidBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const gl = cv.getContext('webgl', {
      antialias: false,
      alpha: false,
      powerPreference: 'low-power',
    });
    if (!gl) {
      document.body.style.background =
        'radial-gradient(80% 60% at 50% 30%, #141c22, #0C1013)';
      return;
    }

    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const U = {
      res: gl.getUniformLocation(prog, 'u_res'),
      time: gl.getUniformLocation(prog, 'u_time'),
      mouse: gl.getUniformLocation(prog, 'u_mouse'),
      amp: gl.getUniformLocation(prog, 'u_amp'),
    };

    const DPR = Math.min(window.devicePixelRatio || 1, 1.5);
    let W = 0;
    let H = 0;
    // The background must fill the whole screen in a standalone iOS PWA, where
    // lvh/dvh under-report and leave the flat body colour showing as a black gap
    // below the composer. Measure the real viewport height in JS and keep the
    // tallest seen (so the software keyboard — which shrinks innerHeight — can't
    // collapse it). CSS reads it via the --app-vh custom property.
    let maxVH = 0;
    const timers: number[] = [];
    const setAppVH = () => {
      const h = window.innerHeight;
      if (h > maxVH) maxVH = h;
      document.documentElement.style.setProperty('--app-vh', maxVH + 'px');
    };
    const resize = () => {
      setAppVH();
      W = cv.clientWidth * DPR;
      H = cv.clientHeight * DPR;
      cv.width = W;
      cv.height = H;
      gl.viewport(0, 0, W, H);
    };
    const onOrient = () => {
      maxVH = 0; // a new orientation has a new full height
      resize();
      timers.push(window.setTimeout(resize, 300)); // re-measure once rotation settles
    };
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', onOrient);
    resize();
    timers.push(window.setTimeout(resize, 300)); // catch the settled standalone viewport

    const mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };
    const onMove = (e: PointerEvent) => {
      mouse.tx = e.clientX / window.innerWidth;
      mouse.ty = 1 - e.clientY / window.innerHeight;
    };
    window.addEventListener('pointermove', onMove);

    let t0 = performance.now();
    let tSec = 0;
    let lastDraw = 0;
    const FRAME = 1000 / 30; // cap GPU draws at ~30fps; the smoke drifts slowly
    const tick = () => {
      if (document.hidden) {
        t0 = performance.now();
        return;
      }
      const now = performance.now();
      if (!RM) tSec += (now - t0) / 1000;
      t0 = now;
      mouse.x += (mouse.tx - mouse.x) * 0.06;
      mouse.y += (mouse.ty - mouse.y) * 0.06;
      if (now - lastDraw < FRAME) return; // throttle the actual render
      lastDraw = now;
      gl.uniform2f(U.res, W, H);
      gl.uniform1f(U.time, tSec);
      gl.uniform2f(U.mouse, mouse.x, mouse.y);
      gl.uniform1f(U.amp, RM ? 0 : 1);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    gsap.ticker.add(tick);

    return () => {
      timers.forEach(clearTimeout);
      gsap.ticker.remove(tick);
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', onOrient);
      window.removeEventListener('pointermove', onMove);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return <canvas id="gl" ref={canvasRef} />;
}
